import type {ApiService} from "~/services/ApiService";
import type {
  Creditor,
  CreditorPaginationResponseObject, RemoveUserFromCompanyRequest,
  User,
  UserPaginationResponseObject,
  UserRemoveAffectResponse
} from '~~/api/models';

export class CompanyService {
  private apiService: ApiService;

  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }

  async getUser(userId: string): Promise<User> {
    return this.apiService.get<User>(`/api/company/users/${userId}`, {
      headers: {
        'X-TENANT-ID': this.apiService.activeTenant as string
      }
    });
  }

  async getUsers(userIds: string[] | null = null, page: number = 1, pageSize: number = 15, fields: string = 'id,displayName,email,profilePictureUrl'): Promise<UserPaginationResponseObject> {
    return this.apiService.get<UserPaginationResponseObject>('/api/company/users', {
      headers: {
        'X-TENANT-ID': this.apiService.activeTenant as string
      },
      query: {
        ...(userIds && {userIds}),
        fields,
        page,
        pageSize
      }
    });
  }

  async getUsersWithFields(userIds: string[] | null = null, fields: string = 'id,displayName,email,workStatus,companies(company(displayName,serialNumber))', tenantId?: string): Promise<User[]> {
    const tenant = tenantId ?? this.apiService.activeTenant;
    if (!tenant) return [];
    return this.apiService.get<User[]>("/api/company/users", {
      headers: {
        'X-TENANT-ID': tenant,
      },
      query: {
        ...(userIds && {userIds}),
        fields,
      }
    });
  }

  async getPagedCompanyUsers(tenantId: string, page: number = 1, pageSize: number = 15, searchString: string = '', fields: string = 'id,displayName,email,workStatus,profilePictureUrl'): Promise<UserPaginationResponseObject> {
    return this.apiService.get<UserPaginationResponseObject>("/api/company/pagedCompanyUsers", {
      headers: {
        'X-TENANT-ID': tenantId,
      },
      query: {
        page,
        pageSize,
        fields,
        searchString,
        sort: 'displayName:asc',
      }
    });
  }

  async getCompanyUsers(tenantId: string | null = null, page: number = 1, pageSize: number = 15, searchString: string = '', fields: string = 'id,displayName,email,profilePictureUrl,workStatus'): Promise<UserPaginationResponseObject> {
    return this.apiService.get<UserPaginationResponseObject>('/api/company/users', {
      headers: {
        'X-TENANT-ID': tenantId ?? this.apiService.activeTenant as string
      },
      query: {
        page,
        pageSize,
        fields,
        searchString,
        sort: 'displayName:asc'
      }
    });
  }
  async getCreditors(page: number = 1, pageSize: number = 15, searchString: string = '', fields: string = 'id,name,creditorNumber'): Promise<CreditorPaginationResponseObject> {
    return this.apiService.get<CreditorPaginationResponseObject>("/api/company/creditors", {
      params: {
        page,
        pageSize,
        searchString,
        SortColumn: 'CreditorNumber',
        SortDirection: 'asc',
        fields
      },
      headers: {
        'X-TENANT-ID': this.apiService.activeTenant as string,
      }
    });
  }

  async getCreditor(id: string, fields: string = 'id,name,creditorNumber'): Promise<Creditor> {
    return this.apiService.get<Creditor>(`/api/company/creditors/${id}`, {
      params: {
        fields
      },
      headers: {
        'X-TENANT-ID': this.apiService.activeTenant as string,
      }
    });
  }

  async getRemoveUserAffects(companyId: string, userId: string): Promise<UserRemoveAffectResponse> {
    return this.apiService.get<UserRemoveAffectResponse>(`/api/company/${companyId}/getremoveaffects/${userId}`, {
      headers: {
        'X-TENANT-ID': this.apiService.activeTenant as string,
      }
    })
  }

  async removeUserFromCompany(request: RemoveUserFromCompanyRequest): Promise<boolean> {
    return this.apiService.post<boolean>('/api/user/removefromcompany', {
      body: request
    })
  }

}